/**
 * FallbackPatternStep Component - For FALLBACK pattern configuration
 * Used when user selected "Sometimes data is missing, show a default value"
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Shield, CheckCircle2, ArrowRight, Lightbulb, Code, Eye, Copy, Zap, TestTube, AlertCircle } from 'lucide-react'
import { WizardState } from '../../../../types/macros'
import DataKeyInput from './shared/DataKeyInput'

export interface FallbackPatternStepProps {
  wizardState: WizardState
  onUpdateState: (updates: Partial<WizardState>) => void
  onBack: () => void
}

const FallbackPatternStep: React.FC<FallbackPatternStepProps> = ({
  wizardState,
  onUpdateState,
  onBack
}) => {
  const [localState, setLocalState] = useState({
    dataKey: wizardState.patternData.fallback?.dataKey || '',
    fallbackValue: wizardState.patternData.fallback?.fallbackValue || '',
    description: wizardState.patternData.fallback?.description || ''
  })
  const [testValue, setTestValue] = useState('')
  const [showPreview, setShowPreview] = useState(false)
  const [testMode, setTestMode] = useState<'with-data' | 'without-data'>('with-data')

  // Update parent state when local state changes
  React.useEffect(() => {
    onUpdateState({
      patternData: {
        ...wizardState.patternData,
        fallback: {
          dataKey: localState.dataKey,
          fallbackValue: localState.fallbackValue,
          description: localState.description
        }
      }
    })
  }, [localState, onUpdateState, wizardState.patternData])

  // Smart fallback suggestions based on data key
  const fallbackSuggestions = React.useMemo(() => {
    const key = localState.dataKey.toLowerCase()
    
    if (key.includes('name') || key.includes('first')) {
      return ['Guest', 'Friend', 'Valued Customer', 'There']
    }
    if (key.includes('email') || key.includes('username')) {
      return ['User', 'Customer', 'Member']
    }
    if (key.includes('tier') || key.includes('level')) {
      return ['Standard', 'Member', 'Valued']
    }
    if (key.includes('points') || key.includes('balance')) {
      return ['0', 'N/A', 'TBD']
    }
    
    return ['Guest', 'N/A', 'Default', 'TBD']
  }, [localState.dataKey])

  // Validation
  const isValid = React.useMemo(() => {
    return localState.dataKey.trim() !== '' && 
           /^[a-zA-Z][a-zA-Z0-9_]*$/.test(localState.dataKey) &&
           localState.fallbackValue.trim() !== ''
  }, [localState.dataKey, localState.fallbackValue])

  // Generate syntax
  const generatedSyntax = React.useMemo(() => {
    if (!localState.dataKey || !localState.fallbackValue) return ''
    return `{{${localState.dataKey}|'${localState.fallbackValue}'}}`
  }, [localState.dataKey, localState.fallbackValue])

  // Interactive templates
  const templates = [
    {
      id: 'greeting',
      title: 'Personal Greeting with Fallback',
      description: 'Greet customers by name, or use friendly fallback',
      dataKey: 'first_name',
      fallbackValue: 'Friend',
      example: "Hi {{first_name|'Friend'}}, welcome back!",
      withData: { value: 'Sarah', result: 'Hi Sarah, welcome back!' },
      withoutData: { result: 'Hi Friend, welcome back!' }
    },
    {
      id: 'location',
      title: 'Location-Based Message',
      description: 'Show city-specific content with default',
      dataKey: 'city',
      fallbackValue: 'your area',
      example: "Special offers in {{city|'your area'}}!",
      withData: { value: 'New York', result: 'Special offers in New York!' },
      withoutData: { result: 'Special offers in your area!' }
    },
    {
      id: 'tier',
      title: 'Membership Tier',
      description: 'Reference member level with guest fallback',
      dataKey: 'loyalty_tier',
      fallbackValue: 'valued customer',
      example: "Thanks for being a {{loyalty_tier|'valued customer'}}!",
      withData: { value: 'Gold member', result: 'Thanks for being a Gold member!' },
      withoutData: { result: 'Thanks for being a valued customer!' }
    },
    {
      id: 'product',
      title: 'Product Recommendation',
      description: 'Show specific product or general category',
      dataKey: 'recommended_product',
      fallbackValue: 'something special',
      example: "We found {{recommended_product|'something special'}} for you!",
      withData: { value: 'iPhone 15 Pro', result: 'We found iPhone 15 Pro for you!' },
      withoutData: { result: 'We found something special for you!' }
    }
  ]

  const handleTemplateSelect = (template: typeof templates[0]) => {
    setLocalState({
      dataKey: template.dataKey,
      fallbackValue: template.fallbackValue,
      description: template.description
    })
    setTestValue(template.withData.value)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleFallbackSuggestion = (suggestion: string) => {
    setLocalState(prev => ({ ...prev, fallbackValue: suggestion }))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full mb-4">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white mb-2">
          Fallback Pattern Setup
        </h2>
        <p className="text-[#6E6D7A] dark:text-gray-400 max-w-md mx-auto">
          Smart choice! When data is missing, we'll show your fallback text to maintain a professional appearance.
        </p>
      </div>

      {/* Pattern Confirmation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
              You Selected: Fallback Pattern
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              This pattern ensures your messages always look complete by showing a default value 
              when the actual data isn't available.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Interactive Templates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg p-6 border border-orange-200 dark:border-orange-800"
      >
        <div className="flex items-center mb-4">
          <Zap className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400" />
          <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100">
            Fallback Pattern Templates
          </h3>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <motion.button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              whileHover={{ scale: 1.02 }}
              className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-orange-700 hover:border-orange-400 dark:hover:border-orange-500 text-left transition-all"
            >
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {template.title}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {template.description}
              </p>
              
              <div className="space-y-2 text-xs">
                <div className="flex gap-2">
                  <code className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-2 py-1 rounded">
                    {template.dataKey}
                  </code>
                  <code className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                    {template.fallbackValue}
                  </code>
                </div>
                
                <div className="space-y-1 text-gray-500 dark:text-gray-400">
                  <div><span className="font-medium">With data:</span> {template.withData.result}</div>
                  <div><span className="font-medium">Without data:</span> {template.withoutData.result}</div>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>

      {/* Configuration Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white mb-4">
          Configuration
        </h3>

        <div className="space-y-6">
          {/* Data Key Input */}
          <DataKeyInput
            value={localState.dataKey}
            onChange={(value) => setLocalState(prev => ({ ...prev, dataKey: value }))}
            platform={wizardState.platform}
            label="Data Key"
            placeholder="e.g., first_name, loyalty_tier"
            helpText="The data field that might sometimes be missing"
            required={true}
          />

          {/* Fallback Value Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Fallback Value <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={localState.fallbackValue}
              onChange={(e) => setLocalState(prev => ({ ...prev, fallbackValue: e.target.value }))}
              placeholder="e.g., Guest, Valued Customer"
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              This text will appear when the data is missing or empty
            </p>

            {/* Smart Suggestions */}
            {localState.dataKey && fallbackSuggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Smart suggestions for "{localState.dataKey}":
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fallbackSuggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => handleFallbackSuggestion(suggestion)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                        localState.fallbackValue === suggestion
                          ? 'bg-[#FE4215] text-white border-[#FE4215]'
                          : 'bg-gray-100 hover:bg-[#FE4215]/10 text-gray-700 hover:text-[#FE4215] border-gray-200 hover:border-[#FE4215]'
                      }`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Description Input */}
          <div>
            <label className="block text-sm font-semibold text-[#1A2B49] dark:text-white mb-2">
              Description (Optional)
            </label>
            <textarea
              value={localState.description}
              onChange={(e) => setLocalState(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of what this macro does..."
              rows={3}
              className="w-full p-3 border-2 border-gray-200 focus:border-[#FE4215] dark:border-gray-700 dark:focus:border-[#FE4215] rounded-lg focus:outline-none bg-white dark:bg-gray-800 text-[#1A2B49] dark:text-white resize-none"
            />
          </div>
        </div>
      </motion.div>

      {/* Live Syntax Preview */}
      {generatedSyntax && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Code className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                Generated Fallback Syntax
              </h3>
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
            >
              <Eye className="w-4 h-4 mr-1" />
              {showPreview ? 'Hide' : 'Show'} Test
            </button>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <code className="flex-1 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-4 py-3 rounded border font-mono text-sm">
              {generatedSyntax}
            </code>
            <button
              onClick={() => copyToClipboard(generatedSyntax)}
              className="p-2 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
              title="Copy syntax"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          
          {showPreview && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700"
            >
              <div className="flex items-center mb-4">
                <TestTube className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                  Test Both Scenarios
                </h4>
              </div>
              
              <div className="space-y-4">
                {/* Test Mode Toggle */}
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setTestMode('with-data')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      testMode === 'with-data'
                        ? 'bg-white dark:bg-gray-600 text-blue-700 dark:text-blue-300 shadow-sm'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    With Data
                  </button>
                  <button
                    onClick={() => setTestMode('without-data')}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      testMode === 'without-data'
                        ? 'bg-white dark:bg-gray-600 text-orange-700 dark:text-orange-300 shadow-sm'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    Without Data
                  </button>
                </div>
                
                {testMode === 'with-data' ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Test value for "{localState.dataKey}":
                      </label>
                      <input
                        type="text"
                        value={testValue}
                        onChange={(e) => setTestValue(e.target.value)}
                        placeholder={`Enter test value for ${localState.dataKey}...`}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                      <div className="text-sm text-green-700 dark:text-green-400 mb-1">
                        <strong>Result (with data):</strong>
                      </div>
                      <div className="font-medium text-green-900 dark:text-green-100">
                        {testValue || <span className="text-green-600 dark:text-green-400 italic">(enter a test value above)</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm text-orange-700 dark:text-orange-400 mb-1">
                          <strong>Result (without data):</strong>
                        </div>
                        <div className="font-medium text-orange-900 dark:text-orange-100">
                          {localState.fallbackValue || <span className="text-orange-600 dark:text-orange-400 italic">(your fallback value)</span>}
                        </div>
                        <p className="text-xs text-orange-600 dark:text-orange-500 mt-1">
                          This is what users see when the data field is empty or missing
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* How It Works */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4"
      >
        <h3 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2">
          How Fallback Patterns Work
        </h3>
        <div className="space-y-2 text-sm text-indigo-700 dark:text-indigo-400">
          <p>• When data exists: Shows the actual value (e.g., "John")</p>
          <p>• When data is missing: Shows your fallback value (e.g., "Guest")</p>
          <p>• Messages always look complete and professional</p>
          <p>• Perfect for optional fields that enhance personalization</p>
        </div>
      </motion.div>

      {/* Example Scenarios */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4"
      >
        <h4 className="font-semibold text-[#1A2B49] dark:text-white mb-3">
          Example Use Cases
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">✅ Perfect for:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Optional first names ("Guest")</li>
              <li>• Loyalty tiers ("Valued Member")</li>
              <li>• Company names ("Customer")</li>
              <li>• Preference settings ("Standard")</li>
            </ul>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-gray-900 dark:text-gray-300">💡 Pro Tips:</div>
            <ul className="space-y-1 text-gray-600 dark:text-gray-400">
              <li>• Keep fallbacks friendly and inclusive</li>
              <li>• Match the tone of your message</li>
              <li>• Test with your actual data</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#6E6D7A] hover:text-[#1A2B49] dark:text-gray-400 dark:hover:text-white transition-colors"
        >
          ← Back to pattern selection
        </button>

        {isValid && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-sm text-green-600"
          >
            <CheckCircle2 className="w-4 h-4" />
            Ready to preview
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default FallbackPatternStep